import React, { useCallback, useRef, useState } from 'react';
import { UploadCloud, FolderOpen, Loader2 } from 'lucide-react';
import GalleryMediaPicker, { GalleryPickResult } from './GalleryMediaPicker';
import { isImageAsset } from '../lib/mediaGallery';

export function isLikelyImageFile(file: File): boolean {
  return file.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|bmp)$/i.test(file.name);
}

export function imageFilesFromList(list: FileList | File[] | null | undefined): File[] {
  if (!list) return [];
  return Array.from(list).filter(isLikelyImageFile);
}

export function imageFilesFromTransfer(dt: DataTransfer | null): File[] {
  return imageFilesFromList(dt?.files);
}

async function waitIfPromise(value: unknown) {
  if (value && typeof (value as Promise<unknown>).then === 'function') {
    await value;
  }
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Falha ao ler arquivo.'));
    reader.readAsDataURL(file);
  });
}

type IngestHandlers = {
  onImageSelected: (dataUrl: string, meta?: { fromGallery: boolean; title?: string; url?: string }) => void | Promise<void>;
  onVideoSelected?: (url: string, meta?: { title?: string; assetId?: string }) => void | Promise<void>;
};

export function useGalleryMediaIngest({ onImageSelected, onVideoSelected }: IngestHandlers) {
  const [uploading, setUploading] = useState(false);

  const ingestFiles = useCallback(async (files: File[]) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (isLikelyImageFile(file)) {
          const dataUrl = await readFileAsDataUrl(file);
          await waitIfPromise(onImageSelected(dataUrl, {
            fromGallery: false,
            title: file.name.replace(/\.[^.]+$/, '') || undefined,
          }));
        } else if (onVideoSelected && (file.type.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/i.test(file.name))) {
          await waitIfPromise(onVideoSelected(URL.createObjectURL(file), { title: file.name }));
        }
      }
    } catch (err: any) {
      alert(err.message || 'Falha ao carregar o arquivo.');
    } finally {
      setUploading(false);
    }
  }, [onImageSelected, onVideoSelected]);

  return { ingestFiles, uploading };
}

export function ImageDropZone({
  onFiles,
  disabled,
  children,
  style,
  className,
  hint = 'Solte várias imagens aqui',
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  hint?: string;
}) {
  const [over, setOver] = useState(false);

  const onDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    if (![...e.dataTransfer.types].includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setOver(true);
  };

  return (
    <div
      className={className}
      style={{ position: 'relative', ...style }}
      onDragEnter={onDragOver}
      onDragOver={onDragOver}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
      }}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        setOver(false);
        const files = imageFilesFromTransfer(e.dataTransfer);
        if (files.length) onFiles(files);
      }}
    >
      {children}
      {over && !disabled && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 4,
            borderRadius: 16,
            border: '2px dashed #818cf8',
            background: 'rgba(99,102,241,0.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#e0e7ff',
            fontSize: '0.88rem',
            fontWeight: 650,
            pointerEvents: 'none',
            textAlign: 'center',
            padding: 16,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

interface Props {
  onImageSelected: (dataUrl: string, meta?: { fromGallery: boolean; title?: string; url?: string }) => void | Promise<void>;
  onVideoSelected?: (url: string, meta?: { title?: string; assetId?: string }) => void | Promise<void>;
  accept?: 'image' | 'video' | 'both';
  accent?: string;
  compact?: boolean;
  dense?: boolean;
  stacked?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  ingestFiles?: (files: File[]) => Promise<void> | void;
  uploading?: boolean;
}

/**
 * Dual-action: open org gallery OR attach a local file (local only — not saved to gallery).
 */
export default function MediaSourceButtons({
  onImageSelected,
  onVideoSelected,
  accept = 'image',
  accent = '#6366f1',
  compact = false,
  dense = false,
  stacked = false,
  disabled = false,
  multiple = false,
  ingestFiles: ingestFilesProp,
  uploading: uploadingProp,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const internal = useGalleryMediaIngest({ onImageSelected, onVideoSelected });
  const ingestFiles = ingestFilesProp || internal.ingestFiles;
  const uploading = uploadingProp ?? internal.uploading;

  const applyResult = async (result: GalleryPickResult) => {
    if (isImageAsset(result.asset) && result.dataUrl) {
      await waitIfPromise(onImageSelected(result.dataUrl, {
        fromGallery: true,
        title: result.asset.title || undefined,
        url: result.asset.video_url,
      }));
      return;
    }
    if (!isImageAsset(result.asset) && onVideoSelected) {
      await waitIfPromise(onVideoSelected(result.asset.video_url, {
        title: result.asset.title || undefined,
        assetId: result.asset.id,
      }));
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    await ingestFiles(files);
  };

  const btn = (primary: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: dense ? 4 : compact ? 6 : 8,
    padding: dense ? '0.28rem 0.5rem' : compact ? '0.45rem 0.8rem' : '0.65rem 1.1rem',
    borderRadius: dense ? 8 : 10,
    border: primary ? `1px solid ${accent}55` : '1px solid rgba(255,255,255,0.12)',
    background: primary ? `${accent}22` : 'rgba(0,0,0,0.35)',
    color: primary ? accent : 'rgba(255,255,255,0.85)',
    fontSize: dense ? '0.65rem' : compact ? '0.78rem' : '0.88rem',
    fontWeight: 600,
    cursor: disabled || uploading ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
    opacity: disabled || uploading ? 0.55 : 1,
    width: stacked ? '100%' : undefined,
  });

  return (
    <>
      <div style={{
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        gap: dense || stacked ? 6 : 8,
        flexWrap: stacked ? 'nowrap' : 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        width: stacked ? '100%' : undefined,
      }}>
        <button type="button" disabled={disabled || uploading} onClick={() => setPickerOpen(true)} style={btn(true)}>
          <FolderOpen size={dense ? 12 : compact ? 14 : 16} /> Galeria
        </button>
        <label style={{ ...btn(false), position: 'relative', margin: 0 }}>
          {uploading ? <Loader2 size={dense ? 12 : compact ? 14 : 16} className="spin" /> : <UploadCloud size={dense ? 12 : compact ? 14 : 16} />}
          {uploading ? 'Carregando…' : 'Upload'}
          <input
            ref={fileRef}
            type="file"
            multiple={multiple}
            disabled={disabled || uploading}
            onChange={handleUpload}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'inherit', fontSize: 16 }}
          />
        </label>
      </div>
      <GalleryMediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={applyResult}
        accept={accept}
        multiple={multiple}
        title={accept === 'image' ? 'Anexar fotos da galeria' : accept === 'video' ? 'Anexar vídeo da galeria' : 'Anexar mídia'}
        saveUploadsToGallery={false}
      />
      <style>{`.spin { animation: spin 0.8s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
